import { Box, DialogContent, Stack, type Theme } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { useNearbyCancellationProtectionPayouts } from "@src/appV2/redesign/CancellationProtectionPayout/api/useNearbyCancellationProtectionPayouts";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import {
  LoadingSpinner,
  PageHeaderWrapper,
  Tab,
  Tabs,
  Text,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useLayoutEffect, useState } from "react";

import {
  DEFAULT_PAYOUT_LOOKBACK_DAYS,
  useCancellationProtectionConfig,
} from "./hooks/useCancellationProtectionConfig";
import { OthersReceivingProtectionList } from "./OthersReceivingProtectionList";
import { TermsTabContent } from "./TermsTabContent";

const TERMS_TAB = { label: "Terms", value: "terms" } as const;
const PAYOUTS_TAB = { label: "Recent protections", value: "payouts" } as const;
const MIN_RESULTS_TO_SHOW_PAYOUTS_TAB = 5;

type TabValue = typeof TERMS_TAB.value | typeof PAYOUTS_TAB.value;

interface CancellationProtectionTermsDialogProps {
  modalState: UseModalState;
  initialTab?: TabValue;
  onNearbyPayoutsAvailableChange?: (available: boolean) => void;
}

export function CancellationProtectionTermsDialog(props: CancellationProtectionTermsDialogProps) {
  const { modalState, initialTab = "terms", onNearbyPayoutsAvailableChange } = props;
  const [activeTab, setActiveTab] = useState<TabValue>(initialTab);
  const worker = useDefinedWorker();
  const cancellationProtectionConfig = useCancellationProtectionConfig();
  const isFeatureEnabled = cancellationProtectionConfig.enabled;
  const isBannerEnabled = isFeatureEnabled && cancellationProtectionConfig.isBannerEnabled;
  const payoutLookbackDays =
    "payoutLookbackDays" in cancellationProtectionConfig
      ? (cancellationProtectionConfig.payoutLookbackDays ?? DEFAULT_PAYOUT_LOOKBACK_DAYS)
      : DEFAULT_PAYOUT_LOOKBACK_DAYS;

  // Sync active tab with initialTab each time the dialog opens.
  useLayoutEffect(() => {
    if (modalState.modalIsOpen) {
      setActiveTab(initialTab);
    }
  }, [modalState.modalIsOpen, initialTab]);

  useLogEffect(
    APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_TERMS_DIALOG_VIEWED,
    { workerId: worker.userId },
    { enabled: modalState.modalIsOpen }
  );

  const isPayoutsQueryEnabled = isBannerEnabled && modalState.modalIsOpen;

  const { payouts, totalCountLabel, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useNearbyCancellationProtectionPayouts({
      lookbackDays: payoutLookbackDays,
      workplaceMsa: worker.address?.metropolitanStatisticalArea,
      enabled: isPayoutsQueryEnabled,
    });

  // Show spinner instead of tabs until we know if payouts tab has data
  const isWaitingForPayoutsTab = initialTab === "payouts" && isLoading;
  const shouldShowPayoutsTab = payouts.length >= MIN_RESULTS_TO_SHOW_PAYOUTS_TAB;
  const tabs: Array<{ label: string; value: TabValue }> = shouldShowPayoutsTab
    ? [TERMS_TAB, PAYOUTS_TAB]
    : [TERMS_TAB];

  useLayoutEffect(() => {
    if (!isPayoutsQueryEnabled || isLoading) {
      return;
    }

    onNearbyPayoutsAvailableChange?.(shouldShowPayoutsTab);

    if (!shouldShowPayoutsTab && activeTab === PAYOUTS_TAB.value) {
      setActiveTab(TERMS_TAB.value);
    }
  }, [
    isPayoutsQueryEnabled,
    isLoading,
    shouldShowPayoutsTab,
    activeTab,
    onNearbyPayoutsAvailableChange,
  ]);

  return (
    <FullScreenDialog skipTopSafeAreaPadding modalState={modalState} variant="tertiary">
      <PageHeaderWrapper variant="tertiary">
        <PageHeaderTopRow onClickBack={modalState.closeModal} />
      </PageHeaderWrapper>

      <Box>
        <Stack spacing={2} sx={{ paddingBottom: 8, paddingX: 5 }}>
          <Title
            component="h1"
            variant="h3"
            sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
          >
            Cancellation Protection
          </Title>
          <Text variant="body2" sx={(theme: Theme) => ({ color: theme.palette.text.tertiary })}>
            Know that your time is always valued and your earnings protected when a workplace
            cancels you last minute
          </Text>
        </Stack>

        {!isWaitingForPayoutsTab && (
          <Tabs
            variant="underlined"
            value={activeTab}
            sx={{
              "& .MuiTabs-flexContainer": { gap: 6, paddingX: 5 },
              "& .MuiTab-root": {
                flexGrow: 0,
                paddingX: 0,
                minWidth: "auto",
              },
              marginBottom: "-2px",
            }}
            onChange={(_, value: string) => {
              let tab: TabValue;
              // eslint-disable-next-line unicorn/prefer-ternary -- Better readability than ternary
              if (value === TERMS_TAB.value || value === PAYOUTS_TAB.value) {
                tab = value;
              } else {
                tab = TERMS_TAB.value;
              }
              setActiveTab(tab);
              logEvent(APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_TERMS_DIALOG_TAB_CHANGED, {
                workerId: worker.userId,
                tab,
              });
            }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} variant="underlined" label={tab.label} value={tab.value} />
            ))}
          </Tabs>
        )}
      </Box>

      <DialogContent
        sx={(theme: Theme) => ({
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          backgroundColor: theme.palette.background.quaternary,
          "&.MuiDialogContent-root": { paddingX: 0, paddingY: 0 },
        })}
      >
        {isWaitingForPayoutsTab ? (
          <Stack sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
            <LoadingSpinner size="medium" />
          </Stack>
        ) : null}
        {!isWaitingForPayoutsTab && activeTab === TERMS_TAB.value ? (
          <TermsTabContent workerId={worker.userId} />
        ) : null}
        {!isWaitingForPayoutsTab && shouldShowPayoutsTab && activeTab === PAYOUTS_TAB.value ? (
          <OthersReceivingProtectionList
            payouts={payouts}
            totalCountLabel={totalCountLabel}
            lookbackDays={payoutLookbackDays}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
          />
        ) : null}
      </DialogContent>
    </FullScreenDialog>
  );
}
